import { logout } from "@/utils/auth";
import {
  AUTH_USER_ID_COOKIE,
  clearAuthCookies,
  getCookie,
  setCookie,
} from "@/utils/cookies";
import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// ✅ Updated type to match your API response structure
type RefreshTokensResponse = {
  statusCode: number;
  message: string;
  data: {
    tokens: {
      accessToken: string;
      idToken: string;
      expiresIn: number;
    };
  };
};

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.NEXT_PUBLIC_BASE_URL,
  prepareHeaders: (headers) => {
    const token = getCookie("accessToken");
    if (token && typeof token === "string") {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

export const baseQueryWithReauth = async (
  args: any,
  api: any,
  extraOptions: any
) => {
  // 🔹 First API call
  let result = await baseQuery(args, api, extraOptions);

  // Skip reauth for login/refresh endpoints
  const isAuthEndpoint = args.url?.includes("/auth/");

  if (result?.error?.status === 401 && !isAuthEndpoint) {
    const refreshToken = getCookie("refreshToken");

    // If no refresh token → logout
    if (!refreshToken || typeof refreshToken !== "string") {
      clearAuthCookies();
      logout();
      return result;
    }

    // 🔹 Try refreshing tokens
    const refreshResult = await baseQuery(
      {
        url: "/auth/refresh-tokens",
        method: "POST",
        body: { refreshToken },
      },
      api,
      extraOptions
    );

    const refreshData = refreshResult.data as RefreshTokensResponse;
    const tokens = refreshData?.data?.tokens;

    // 🔹 If tokens received successfully
    if (tokens?.accessToken) {
      const meResult = await baseQuery(
        {
          url: "/youth/me",
          method: "GET",
          headers: {
            Authorization: `Bearer ${tokens.accessToken}`,
          },
        },
        api,
        extraOptions
      );

      const refreshedUserId = (meResult?.data as any)?.id;
      const expectedUserId = getCookie(AUTH_USER_ID_COOKIE);

      if (meResult?.error || !refreshedUserId) {
        clearAuthCookies();
        logout();
        return result;
      }

      if (expectedUserId && refreshedUserId && expectedUserId !== refreshedUserId) {
        clearAuthCookies();
        logout();
        return result;
      }

      // Save tokens to cookies (7 days example)
      setCookie("accessToken", tokens.accessToken, 7);
      setCookie("idToken", tokens.idToken, 7);
      if (refreshedUserId) {
        setCookie(AUTH_USER_ID_COOKIE, refreshedUserId, 30);
      }

      // 🔁 Retry original request with new token
      result = await baseQuery(
        {
          ...args,
          headers: {
            ...args.headers,
            Authorization: `Bearer ${tokens.accessToken}`,
          },
        },
        api,
        extraOptions
      );
    } else {
      // Refresh failed → logout user
      clearAuthCookies();
      logout();
    }
  }

  return result;
};
